import { Node, Edge } from 'reactflow';

import { NodeType } from '../constants';
import { declaresBehavior } from '../registry/declaredNodes';
import { isVegaLiteNodeType } from '../registry/vegaLiteNodes';
import { classifyAutkSpecString } from './autkSpecKind';
import { getFlowNodeCanonicalType } from './flowNodeCanonicalType';
import { hideNode } from './hiddenNodes';

interface NodeWithPosition extends Node {
  position: { x: number; y: number };
}

/**
 * The element a dashboard tile can be dragged by, while its layout is unlocked.
 *
 * React Flow takes a selector; the tile's title band carries the same class
 * without the dot. Restricting the handle means a click inside a chart or a
 * table does not move the tile out from under the pointer.
 */
export const DASHBOARD_TILE_DRAG_HANDLE = '.curio-dashboard-tile-handle';

/**
 * The size a tile gets when nobody has resized it on the dashboard.
 *
 * Deliberately much larger than a canvas node. A node is sized to be legible
 * among fifty others on a canvas; a tile is the content of a page, and at node
 * size a single pinned chart floated in a corner of the window looking like a
 * node that had lost its canvas. The page fits every tile into the window, so
 * a generous default costs nothing when there are many: they scale down
 * together. It is only a default, and `dashboardWidth`/`dashboardHeight` win.
 */
export const DASHBOARD_TILE_DEFAULT_WIDTH = 880;
export const DASHBOARD_TILE_DEFAULT_HEIGHT = 560;

const START_Y = 50;
const V_GAP = 20;
/** Gutter between auto-laid columns and their neighbours. */
const H_GAP = 40;

/**
 * What a tile will actually be drawn at, which is what the auto-layout has to
 * reserve room for. Mirrors the fallback chain `UniversalNode` uses for a
 * dashboard tile, so the two never disagree about how wide a column is.
 */
function tileWidth(node: NodeWithPosition): number {
  return node.data?.dashboardWidth ?? DASHBOARD_TILE_DEFAULT_WIDTH;
}

function tileHeight(node: NodeWithPosition): number {
  return node.data?.dashboardHeight ?? DASHBOARD_TILE_DEFAULT_HEIGHT;
}

/**
 * Pinned tiles in columns, given in order: left to right from *baseX*, each
 * column stacked top to bottom. A tile with a saved slot stays in it.
 *
 * Columns advance by the width of the widest tile in the column just placed,
 * rather than by one fixed pitch. A fixed pitch narrower than a tile overlapped
 * its neighbour, and the owner had to drag them apart before the dashboard was
 * readable at all.
 */
function placeColumns(
  unpinned: NodeWithPosition[],
  columns: NodeWithPosition[][],
  baseX: number,
): NodeWithPosition[] {
  const updatedNodes: NodeWithPosition[] = [...unpinned];
  let cursorX = baseX;
  for (const column of columns) {
    let stackY = START_Y;
    for (const node of column) {
      // If node has a saved dashboard position, use it; otherwise auto-layout
      if (typeof node.data?.dashboardX === "number") {
        updatedNodes.push({ ...node, position: { x: node.data.dashboardX, y: node.data.dashboardY } });
      } else {
        updatedNodes.push({ ...node, position: { x: cursorX, y: stackY } });
        stackY += tileHeight(node) + V_GAP;
      }
    }
    cursorX += Math.max(...column.map(tileWidth)) + H_GAP;
  }
  return updatedNodes;
}

/** Ordered lists of pinned tile ids, one per column, left to right. */
export type DashboardColumns = readonly (readonly string[])[];

// Function to apply a dashboard layout to nodes based on their pinned status
// Pinned nodes are arranged in a grid-like structure based on their distances from root nodes,
// or in the *columns* given (the dashboard by scenario, #662).
export function applyDashboardLayout(
  nodes: NodeWithPosition[],
  edges: readonly Edge[],
  dashboardPins: { [key: string]: boolean },
  columns?: DashboardColumns | null,
): NodeWithPosition[] {
  // If there are no nodes or no pinned nodes, return the original nodes
  if (!nodes.length || !Object.values(dashboardPins).some(Boolean)) return nodes;

  if (columns && columns.length > 0) {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const placed = new Set<string>();
    const groups = columns
      .map((ids) => ids.filter((id) => {
        if (!dashboardPins[id] || !byId.has(id) || placed.has(id)) return false;
        placed.add(id);
        return true;
      }).map((id) => byId.get(id)!))
      .filter((group) => group.length > 0);
    // A pinned tile no column names still gets a place, in a column of its own.
    const rest = nodes.filter((node) => dashboardPins[node.id] && !placed.has(node.id));
    if (rest.length > 0) groups.push(rest);
    const where = (node: NodeWithPosition) => node.data?.workflowPosition ?? node.position;
    const baseX = Math.min(...groups.flat().map((node) => where(node).x));
    return placeColumns(nodes.filter((node) => !dashboardPins[node.id]), groups, baseX);
  }

  // The nodes are divided into pinned and unpinned categories to calc the horizontal positions
  const pinnedNodes = nodes.filter(node => dashboardPins[node.id]);
  const unpinnedNodes = nodes.filter(node => !dashboardPins[node.id]);

  // If there are no pinned nodes, return the unpinned nodes
  if (pinnedNodes.length > 0) {
    const adjacencyMap = new Map<string, string[]>();
    // Create an adjacency map to track connections between nodes
    nodes.forEach(node => adjacencyMap.set(node.id, []));
    edges.forEach(edge => adjacencyMap.get(edge.source)?.push(edge.target));
    // Find root nodes (nodes that are not targets of any edge) and calculate distances
    const rootNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id) && dashboardPins[node.id]);
    const pinnedDistances = new Map<string, number>();
    // Function to calculate distances from root nodes to pinned nodes
    const calculateDistances = (nodeId: string, distance: number, visited: Set<string>) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      if (dashboardPins[nodeId] && !pinnedDistances.has(nodeId)) {
        pinnedDistances.set(nodeId, distance);
      }
      const neighbors = adjacencyMap.get(nodeId) || [];
      const nextDistance = dashboardPins[nodeId] ? distance + 1 : distance;
      neighbors.forEach(neighborId => calculateDistances(neighborId, nextDistance, visited));
    };
    // Start calculating distances from each root node
    rootNodes.forEach(rootNode => calculateDistances(rootNode.id, 0, new Set<string>()));
    const distanceGroups = new Map<number, NodeWithPosition[]>();
    pinnedNodes.forEach(node => {
      const distance = pinnedDistances.get(node.id) ?? 0;
      if (!distanceGroups.has(distance)) distanceGroups.set(distance, []);
      distanceGroups.get(distance)?.push(node);
    });
    const baseX = Math.min(...pinnedNodes.map(n => n.position.x));
    const groups = Array.from(distanceGroups.keys()).sort((a, b) => a - b).map(distance => {
      const group = distanceGroups.get(distance) ?? [];
      // Sort column by original Y for consistent ordering
      return group.sort((a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0));
    });
    return placeColumns(unpinnedNodes, groups, baseX);
  }
  return unpinnedNodes;
}


/**
 * Lay a loaded dataflow out as a dashboard.
 *
 * Pure: it never mutates its arguments, and it is the only place that decides
 * what a dashboard's node list looks like. Three things happen here.
 *
 * ``data.workflowPosition`` is stamped on every node first. ``TrillGenerator``
 * saves that in preference to ``position``, so a save made from the dashboard
 * keeps the canvas coordinates the dataflow was authored with rather than
 * writing tile positions over them.
 *
 * Pinned nodes move to their saved slot (``dashboardX``/``dashboardY``) or, when
 * they have none, to the automatic layout: in *columns* when the dataflow's
 * scenarios give them (``scenarioDashboard``, #662), else by distance.
 *
 * Unpinned nodes stay MOUNTED and are hidden with ``display: none``
 * (``hideNode``). React Flow's own ``hidden`` unmounts the node component, which
 * would break every chain that runs through an unpinned Data Pool: its
 * behaviour hook is what re-derives a tile's data from the restored outputs.
 * Edges are hidden the ordinary way, since nothing depends on an edge being
 * rendered.
 */
export function prepareDashboardNodes<N extends Node, E extends Edge>(
  nodes: readonly N[],
  edges: readonly E[],
  dashboardPins: { [key: string]: boolean },
  columns?: DashboardColumns | null,
): { nodes: N[]; edges: E[] } {
  const stamped = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      workflowPosition: node.data?.workflowPosition ?? { ...node.position },
    },
  })) as unknown as NodeWithPosition[];

  const laidOut = applyDashboardLayout(stamped, edges, dashboardPins, columns);

  const dashboardNodes = laidOut.map((node) => {
    if (dashboardPins[node.id]) {
      return {
        ...node,
        style: undefined,
        // `draggable` and `selectable` are deliberately NOT set here. React
        // Flow reads a node's own flag in preference to the canvas-wide one
        // (`node.draggable || (nodesDraggable && node.draggable === undefined)`),
        // so setting either to true would make a tile movable while the
        // dashboard's layout is locked, which is every viewer's state.
        dragHandle: DASHBOARD_TILE_DRAG_HANDLE,
      };
    }
    return hideNode(node);
  }) as unknown as N[];

  return {
    nodes: dashboardNodes,
    edges: edges.map((edge) => ({ ...edge, hidden: true })) as E[],
  };
}

/**
 * Where Arrange by scenario puts each pinned tile: its place in *columns*,
 * whatever slot it was saved in or dragged to.
 */
export function arrangedTilePositions(
  nodes: readonly Node[],
  dashboardPins: { [key: string]: boolean },
  columns: DashboardColumns,
): Map<string, { x: number; y: number }> {
  const unsaved = nodes.map((node) =>
    dashboardPins[node.id]
      ? { ...node, data: { ...node.data, dashboardX: undefined, dashboardY: undefined } }
      : node,
  ) as NodeWithPosition[];
  const laidOut = applyDashboardLayout(unsaved, [], dashboardPins, columns);
  return new Map(
    laidOut.filter((node) => dashboardPins[node.id]).map((node) => [node.id, node.position]),
  );
}

/** The behaviors of the nodes that pass data through rather than produce it. */
const PASS_THROUGH_BEHAVIORS: ReadonlySet<string> = new Set(['vega', 'simple-vis', 'data-pool']);

/** The built-in nodes that pass data through, for a type no template declares a behavior for. */
export const PASS_THROUGH_NODE_TYPES: ReadonlySet<string> = new Set([
  NodeType.VIS_VEGA,
  NodeType.VIS_SIMPLE,
  NodeType.DATA_POOL,
]);

/** The Autark node, a pass-through only when its spec draws. */
const AUTK_GRAMMAR_BEHAVIORS: ReadonlySet<string> = new Set(['autk-grammar']);
const AUTK_GRAMMAR_NODE_TYPES: ReadonlySet<string> = new Set([NodeType.AUTK_GRAMMAR]);

/**
 * Nodes that pass data through rather than produce it: a node whose template
 * declares a Vega-Lite chart's, a Simple View's or a Data Pool's behavior
 * (registry/declaredNodes), or draws with the Vega-Lite grammar
 * (registry/vegaLiteNodes).
 *
 * Each of these re-derives what it shows from ``data.input`` whenever that
 * changes, which is what a project load does through ``hydrateRestoredOutputs``.
 * So a dashboard does not need their output saved: it needs the output of
 * whatever feeds them. Everything else makes new data, and that is what has to
 * be in the Data Catalog for a tile to render without a run.
 *
 * An Autark node is only a pass-through when its spec draws (``map``/``plot``);
 * its data and compute forms produce layers, so they are sources. Mirrors
 * `passes_through` in `projects/dashboard_payload.py`.
 */
export function isPassThroughNode(node: { type?: string | null; data?: any }): boolean {
  const nodeType = getFlowNodeCanonicalType(node as any);
  if (
    declaresBehavior(nodeType, PASS_THROUGH_BEHAVIORS, PASS_THROUGH_NODE_TYPES) ||
    isVegaLiteNodeType(nodeType)
  ) {
    return true;
  }
  if (declaresBehavior(nodeType, AUTK_GRAMMAR_BEHAVIORS, AUTK_GRAMMAR_NODE_TYPES)) {
    const spec = node.data?.code ?? node.data?.defaultCode;
    return classifyAutkSpecString(spec) === 'render';
  }
  return false;
}

/**
 * The nodes whose outputs a dashboard needs saved.
 *
 * Walking up from each pinned tile, the first node on every path that is not a
 * pass-through is where that tile's data is actually made. Those are the nodes
 * whose outputs go to the Data Catalog, so a reload can hand the tile its data
 * without anyone pressing Run.
 *
 * A pinned node's own output is not in the set. What a tile renders comes from
 * its input; a node that renders its own run output instead (a code node's
 * stdout pane, a Data Summary's table) has nothing a saved dataset could
 * restore, so saving it would add a dataset that nothing reads. A pinned code
 * node with widgets shows them in place of its code, as its tile's controls
 * (NodeEditor): what it makes reaches the page through the tiles it feeds,
 * and is saved as theirs.
 */
export function dashboardSourceNodeIds(
  nodes: readonly { id: string; type?: string | null; data?: any }[],
  edges: readonly { source?: unknown; target?: unknown }[],
): Set<string> {
  const pinned = nodes.filter((node) => node.data?.dashboardPinned);
  return producersFeeding(pinned.map((node) => node.id), nodes, edges);
}

/**
 * The nodes that make the data reaching *ids*: walking up from each, the first
 * node on every path that is not a pass-through. *ids* themselves are not in
 * the set. A pinned tile and a scenario's pass-through context or outcome
 * (#662) find what to save this way.
 */
export function producersFeeding(
  ids: readonly string[],
  nodes: readonly { id: string; type?: string | null; data?: any }[],
  edges: readonly { source?: unknown; target?: unknown }[],
): Set<string> {
  const sources = new Set<string>();
  if (ids.length === 0) return sources;

  const byId = new Map(nodes.map((node) => [node.id, node]));
  const incoming = new Map<string, string[]>();
  for (const edge of edges) {
    const target = typeof edge?.target === 'string' ? edge.target : null;
    const source = typeof edge?.source === 'string' ? edge.source : null;
    if (!target || !source) continue;
    const list = incoming.get(target);
    if (list) list.push(source);
    else incoming.set(target, [source]);
  }

  // Breadth-first, with a visited set: a cycle (or a diamond) must terminate.
  const queue = [...ids];
  const visited = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const sourceId of incoming.get(id) ?? []) {
      const node = byId.get(sourceId);
      if (!node) continue;
      if (isPassThroughNode(node)) queue.push(sourceId);
      else sources.add(sourceId);
    }
  }
  return sources;
}
